/**
 * SkillMatrixPage.tsx
 * Employee × Test skill matrix page (Phase 1, Item 4).
 *
 * Layout:
 *   - SkillMatrixToolbar     — filters + undo/redo buttons
 *   - SkillMatrixGrid        — scrollable cross-join table with range selection
 *   - SelectionActionBar     — floating bar when ≥ 2 cells are selected
 *   - Single-cell Popover    — opens on plain click or Enter/F2
 *
 * Selection model (Excel-style):
 *   Click a cell → focused (focus ring). Shift+click → rectangle highlight.
 *   Press 1/2/3/4 → set all selected cells. Backspace/Delete → clear.
 *   Escape → deselect. Arrow keys → move focus. Shift+Arrow → extend.
 *   Enter/F2 → open single-cell popover. Ctrl+A → select all visible.
 *
 * The "Bulk Edit Mode" toggle and row checkboxes are removed.
 * Selection state lives in the store but is excluded from undo history.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Popover,
  Select,
  MenuItem,
  Snackbar,
  Typography,
  FormControl,
} from '@mui/material';
import {
  fetchSkillMatrix,
  fetchSkillMatrixUsers,
  fetchSkillMatrixSkills,
  fetchSkillMatrixLookups,
  updateUserTier,
  type BatchCellUpdate,
  type SkillMatrixCell,
  type SkillUser,
  type MatrixSkill,
  type SkillMatrixLookups,
} from '../../../services/scheduleSkillMatrix';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchUserPhotos } from '../../../services/graphService';
import { SkillMatrixToolbar, type SkillMatrixFilters } from './SkillMatrixToolbar';
import { type Density, readDensity, writeDensity } from './skill-matrix/density';
import { SkillMatrixGrid } from './SkillMatrixGrid';
import { SelectionActionBar } from './skill-matrix/SelectionActionBar';
import {
  useSkillMatrixStore,
  useSkillMatrixHistoryCounts,
  safeUndo,
  safeRedo,
} from '../../../state/skillMatrixStore';
import { useUndoRedoHotkeys } from './skill-matrix/useUndoRedoHotkeys';
import { useRangeSelection } from './skill-matrix/useRangeSelection';
import { useSkillMatrixHotkeys } from './skill-matrix/useSkillMatrixHotkeys';
import { resolveSelectedCoords } from './skill-matrix/selectionMath';
import type { CellCoord } from './skill-matrix/selectionMath';
import { useDragFill } from './skill-matrix/useDragFill';
import { SKILL_LEVELS, SKILL_LEVEL_META, MAX_SKILL_LEVEL } from '../../../constants/scheduleEnums';

// Sentinel used to distinguish the "Clear" MenuItem from the numeric levels.
const CLEAR_SENTINEL = '__clear__';

interface CellEditorState {
  anchorEl: HTMLElement;
  cell: SkillMatrixCell;
}

interface ToastState {
  message: string;
  severity: 'success' | 'error';
}

const DEFAULT_FILTERS: SkillMatrixFilters = {
  jobClass: '',
  workGroup: '',
  category: '',
  search: '',
};

export const SkillMatrixPage: React.FC = () => {
  const { instance: msalInstance } = useMsal();

  // ── Store-backed cells (zustand + zundo) ──────────────────────────────────
  const cells = useSkillMatrixStore((s) => s.cells);
  const selection = useSkillMatrixStore((s) => s.selection);
  const { loadCellsFromServer, applyAndPersist } = useSkillMatrixStore.getState();

  // History counts — reactive via temporal store subscription.
  const [, forceUpdate] = useState(0);
  const temporalStoreRef = useRef(useSkillMatrixStore.temporal as unknown as {
    subscribe: (l: () => void) => () => void;
    getState: () => { pastStates: unknown[]; futureStates: unknown[] };
  });
  useEffect(() => {
    return temporalStoreRef.current.subscribe(() => forceUpdate((n) => n + 1));
  }, []);
  const historyCounts = useSkillMatrixHistoryCounts();
  const canUndo = historyCounts.pastSize > 0;
  const canRedo = historyCounts.futureSize > 0;

  // ── Local UI state ────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<SkillMatrixFilters>(DEFAULT_FILTERS);
  const [users, setUsers] = useState<SkillUser[]>([]);
  const [skills, setSkills] = useState<MatrixSkill[]>([]);
  const [lookups, setLookups] = useState<SkillMatrixLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [cellEditor, setCellEditor] = useState<CellEditorState | null>(null);
  /** Graph photo blob URLs keyed by email. Populated after users load; non-blocking. */
  const [photos, setPhotos] = useState<Record<string, string | null>>({});

  // ── Density preference (persists to localStorage) ─────────────────────────
  const [density, setDensity] = useState<Density>(() => readDensity());

  const handleDensityChange = useCallback((d: Density) => {
    setDensity(d);
    writeDensity(d);
  }, []);

  // ── Edit mode (BUG-2026-0098) ─────────────────────────────────────────────
  // The matrix opens view-only so a stray click can't change a skill level.
  // The user must click "Edit" to unlock editing; "Done" returns to view-only.
  // Intentionally NOT persisted — each visit starts safe (view-only).
  const [editMode, setEditMode] = useState(false);

  // Tier meta-column editing — admin-only (PUT /users/:key) AND edit mode, so
  // the view-only latch (BUG-2026-0098) covers Tier exactly like skill cells.
  const { isAdmin } = usePermissions();

  const handleTierChange = useCallback(async (appUserKey: string, tier: string | null) => {
    const prevUsers = users;
    // Optimistic: swap the user's Tier in place so the select updates instantly.
    setUsers((us) => us.map((u) => (u.AppUserKey === appUserKey ? { ...u, Tier: tier } : u)));
    try {
      await updateUserTier(appUserKey, tier);
      setToast({ message: tier ? `Tier set to ${tier}` : 'Tier cleared (auto-derive)', severity: 'success' });
    } catch (err) {
      setUsers(prevUsers);
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setToast({ message: msg ?? 'Failed to update tier', severity: 'error' });
    }
  }, [users]);

  const handleToggleEditMode = useCallback(() => {
    // RBAC overhaul 2026-08-06: skill levels are admin-only (review rights
    // derive from them — a technician must not raise their own). The toolbar
    // hides the toggle for non-admins; this guard is belt-and-braces so no
    // other code path can flip a non-admin into edit mode.
    if (!isAdmin) return;
    setEditMode((prev) => {
      const next = !prev;
      // Leaving edit mode: drop any visual selection so the floating action
      // bar and focus ring don't linger over a now-read-only grid.
      if (!next) useSkillMatrixStore.getState().clearSelection();
      return next;
    });
  }, [isAdmin]);

  const showToast = useCallback((message: string, severity: 'success' | 'error') => {
    setToast({ message, severity });
  }, []);

  // ── Keyboard shortcuts — undo/redo (existing hook) ────────────────────────
  // Gated on editMode so Ctrl+Z/Y are inert while the matrix is view-only.
  useUndoRedoHotkeys({ onError: (msg) => showToast(msg, 'error'), enabled: editMode });

  // ── Container ref for the grid (used by useDragFill) ─────────────────────
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  // ── Range selection mouse handlers ────────────────────────────────────────
  const { onCellMouseDown, onCellMouseEnter, onCellClick: onRangeClick } = useRangeSelection();

  // ── Single-cell popover open handler (used by Enter/F2 in hotkeys hook) ───
  const handleCellEditorOpen = useCallback((cell: SkillMatrixCell, anchorEl: HTMLElement) => {
    setCellEditor({ anchorEl, cell });
  }, []);

  // ── Visible task types (filtered by Category) ────────────────────────────
  // Category is a client-side column-scope filter; all cells are loaded from
  // the server regardless of category (see loadMatrix below).
  const visibleSkills = useMemo(
    () => filters.category
      ? skills.filter((tt) => tt.Category === filters.category)
      : skills,
    [skills, filters.category],
  );

  // ── Drag-fill handle (Excel autofill) ────────────────────────────────────
  // Derive the focused cell's level for the fill handle.
  const focusedCellLevel = useMemo(() => {
    if (!selection.focus) return null;
    const found = cells.find(
      (c) =>
        c.UserKey === selection.focus!.userKey &&
        c.SkillKey === selection.focus!.skillKey,
    );
    return found?.Level ?? null;
  }, [cells, selection.focus]);

  const {
    previewRect: dragPreviewRect,
    onHandleMouseDown,
  } = useDragFill({
    users,
    skills: visibleSkills,
    sourceCoord: selection.focus,
    sourceLevel: focusedCellLevel,
    containerRef: gridContainerRef,
  });

  // ── Derived selection count ───────────────────────────────────────────────
  const selectedCoords = useMemo(
    () => resolveSelectedCoords(selection.anchor, selection.focus, users, visibleSkills),
    [selection.anchor, selection.focus, users, visibleSkills],
  );
  const selectedCount = selectedCoords.length;

  // ── Skill Matrix hotkeys (1–4, Del, Esc, arrows, Ctrl+A) ─────────────────
  // Disabled in view-only mode so keyboard edits can't fire either (BUG-2026-0098).
  useSkillMatrixHotkeys({
    users,
    skills: visibleSkills,
    onOpenCellEditor: handleCellEditorOpen,
    onError: (msg) => showToast(msg, 'error'),
    enabled: editMode,
  });

  // ── Toolbar undo/redo handlers ────────────────────────────────────────────
  const handleUndo = useCallback(async () => {
    try { await safeUndo(); }
    catch (e) { showToast(e instanceof Error ? e.message : 'Undo failed', 'error'); }
  }, [showToast]);

  const handleRedo = useCallback(async () => {
    try { await safeRedo(); }
    catch (e) { showToast(e instanceof Error ? e.message : 'Redo failed', 'error'); }
  }, [showToast]);

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      fetchSkillMatrixUsers(),
      fetchSkillMatrixSkills(),
      fetchSkillMatrixLookups(),
    ])
      .then(([u, tt, lk]) => {
        setUsers(u);
        setSkills(tt);
        setLookups(lk);
        // Kick off photo fetch non-blocking — grid renders with initials fallback
        // while photos arrive, then re-renders when the map fills in.
        fetchUserPhotos(
          msalInstance as Parameters<typeof fetchUserPhotos>[0],
          u.map((x) => x.Email),
        )
          .then((photoMap) => {
            const obj: Record<string, string | null> = {};
            photoMap.forEach((url, email) => { obj[email] = url; });
            setPhotos(obj);
          })
          .catch(() => {
            // Non-fatal: Graph might be unavailable or scopes not consented.
            // The avatar component falls back to colored initials automatically.
          });
      })
      .catch((e: unknown) => {
        showToast(e instanceof Error ? e.message : 'Failed to load skill matrix data', 'error');
      });
  }, [showToast, msalInstance]);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      // Category is a client-side column filter — load all cells for the current
      // jobClass/workGroup combination; visibleSkills scopes the columns.
      const data = await fetchSkillMatrix({
        jobClass: filters.jobClass || undefined,
        workGroup: filters.workGroup || undefined,
      });
      loadCellsFromServer(data);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to load skill matrix', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.jobClass, filters.workGroup, showToast, loadCellsFromServer]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  // ── Derived filtered users ────────────────────────────────────────────────
  // Category filter scopes visible test COLUMNS only — all employee rows are
  // always shown regardless of which category is selected.
  const filteredUsers = useMemo(() => users, [users]);

  // ── Filter change handler ─────────────────────────────────────────────────
  const handleFilterChange = useCallback((f: SkillMatrixFilters) => {
    setFilters(f);
    // Clear selection when filters change — coords may become stale.
    useSkillMatrixStore.getState().clearSelection();
  }, []);

  // ── Cell click handler: opens single-cell popover (right-click / Enter/F2) ─
  const handleCellClick = useCallback((cell: SkillMatrixCell, anchorEl: HTMLElement) => {
    setCellEditor({ anchorEl, cell });
  }, []);

  // ── Plain-click cycle handler ─────────────────────────────────────────────
  /**
   * Cycles the level for a single cell: empty → 1 → 2 → 3 → 4 → 5 → empty.
   * One cycle = one undo step (applyAndPersist adds to the zundo history).
   */
  const handleCellCycle = useCallback(async (coord: CellCoord) => {
    if (!editMode) return; // View-only guard (BUG-2026-0098)
    const existing = cells.find(
      (c) => c.UserKey === coord.userKey && c.SkillKey === coord.skillKey,
    );
    const currentLevel = existing?.Level ?? null;
    // >= MAX also catches level 9 (Approver, Phase C): cycling never produces
    // an invalid 6–8 — a designate cell cycles back to empty.
    const nextLevel: number | null =
      currentLevel === null            ? 1 :
      currentLevel >= MAX_SKILL_LEVEL  ? null :
      currentLevel + 1;

    const user = users.find((u) => u.AppUserKey === coord.userKey);
    const tt = visibleSkills.find((t) => t.SkillKey === coord.skillKey);
    const fallback: SkillMatrixCell = existing ?? {
      SkillMatrixKey: null,
      UserKey: coord.userKey,
      UserDisplayName: user?.DisplayName ?? '',
      UserEmail: user?.Email ?? '',
      JobClass: user?.JobClass ?? null,
      WorkGroup: user?.WorkGroup ?? null,
      Tier: user?.Tier ?? null,
      SkillKey: coord.skillKey,
      SkillName: tt?.Name ?? '',
      SkillCategory: tt?.Category ?? null,
      Level: null,
    };

    try {
      await useSkillMatrixStore
        .getState()
        .applyAndPersist(
          [{ UserKey: coord.userKey, SkillKey: coord.skillKey, Level: nextLevel }],
          [fallback],
        );
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to update level', 'error');
    }
  }, [editMode, cells, users, visibleSkills, showToast]);

  const handleCellEditorClose = useCallback(() => setCellEditor(null), []);

  const handleCellLevelChange = useCallback(async (raw: number | string) => {
    if (!editMode) return; // View-only guard (BUG-2026-0098)
    if (!cellEditor) return;
    const { UserKey, SkillKey } = cellEditor.cell;
    const newLevel = raw === CLEAR_SENTINEL ? null : Number(raw);
    setCellEditor(null);

    const payload: BatchCellUpdate[] = [{ UserKey, SkillKey, Level: newLevel }];
    try {
      await applyAndPersist(payload, [cellEditor.cell]);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to update level', 'error');
    }
  }, [editMode, cellEditor, applyAndPersist, showToast]);

  // ── SelectionActionBar callbacks ──────────────────────────────────────────

  /**
   * Apply a level to all currently-selected cells.
   * Builds fallback cells from users/skills for cells not yet in local state.
   */
  const handleActionBarApplyLevel = useCallback(async (level: number) => {
    if (!editMode) return; // View-only guard (BUG-2026-0098)
    if (selectedCoords.length === 0) return;
    const batchPayload: BatchCellUpdate[] = [];
    const fallbacks: SkillMatrixCell[] = [];

    for (const coord of selectedCoords) {
      batchPayload.push({ UserKey: coord.userKey, SkillKey: coord.skillKey, Level: level });
      const existing = cells.find((c) => c.UserKey === coord.userKey && c.SkillKey === coord.skillKey);
      if (existing) {
        fallbacks.push(existing);
      } else {
        const user = users.find((u) => u.AppUserKey === coord.userKey);
        const tt = visibleSkills.find((t) => t.SkillKey === coord.skillKey);
        if (user && tt) {
          fallbacks.push({
            SkillMatrixKey: null,
            UserKey: coord.userKey,
            UserDisplayName: user.DisplayName,
            UserEmail: user.Email,
            JobClass: user.JobClass,
            WorkGroup: user.WorkGroup,
            Tier: user.Tier,
            SkillKey: coord.skillKey,
            SkillName: tt.Name,
            SkillCategory: tt.Category,
            Level: null,
          });
        }
      }
    }

    try {
      await applyAndPersist(batchPayload, fallbacks);
      showToast(`Applied Level ${level} to ${selectedCoords.length} cell${selectedCoords.length === 1 ? '' : 's'}`, 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to apply level', 'error');
    }
  }, [editMode, selectedCoords, cells, users, visibleSkills, applyAndPersist, showToast]);

  const handleActionBarClear = useCallback(async () => {
    if (!editMode) return; // View-only guard (BUG-2026-0098)
    if (selectedCoords.length === 0) return;
    const batchPayload: BatchCellUpdate[] = selectedCoords.map((coord) => ({
      UserKey: coord.userKey,
      SkillKey: coord.skillKey,
      Level: null,
    }));
    try {
      await applyAndPersist(batchPayload, []);
      showToast(`Cleared ${selectedCoords.length} cell${selectedCoords.length === 1 ? '' : 's'}`, 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to clear cells', 'error');
    }
  }, [editMode, selectedCoords, applyAndPersist, showToast]);

  // REQ-07 — people are added in Admin → Users (then made assignable); surface
  // that path from the matrix where schedulers looked for it.
  const navigate = useNavigate();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <SkillMatrixToolbar
        filters={filters}
        skills={skills}
        lookups={lookups}
        onFilterChange={handleFilterChange}
        density={density}
        onDensityChange={handleDensityChange}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
        editMode={editMode}
        onToggleEditMode={handleToggleEditMode}
        canEdit={isAdmin}
        onAddPerson={() => navigate('/admin/users')}
      />

      {/* Grid area — always rendered to preserve scroll position */}
      <Box ref={gridContainerRef} sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        <SkillMatrixGrid
          cells={cells}
          users={filteredUsers}
          skills={visibleSkills}
          search={filters.search}
          density={density}
          readOnly={!editMode}
          photos={photos}
          onCellClick={handleCellClick}
          onCellCycle={handleCellCycle}
          onCellMouseDown={onCellMouseDown}
          onCellMouseEnter={onCellMouseEnter}
          onHandleMouseDown={onHandleMouseDown}
          dragPreviewRect={dragPreviewRect}
          canEditTier={isAdmin && editMode}
          onTierChange={handleTierChange}
        />

        {/* Loading overlay — small pill in top-right during filter refetches */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              boxShadow: 2,
              zIndex: 10,
            }}
          >
            <CircularProgress size={14} thickness={5} />
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
      </Box>

      {/* Floating action bar — appears when ≥ 2 cells are selected (edit mode only) */}
      <SelectionActionBar
        selectedCount={editMode ? selectedCount : 0}
        onApplyLevel={handleActionBarApplyLevel}
        onClear={handleActionBarClear}
      />

      {/* Single-cell Level Editor Popover */}
      <Popover
        open={Boolean(cellEditor)}
        anchorEl={cellEditor?.anchorEl ?? null}
        onClose={handleCellEditorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={700}>
            Set Skill Level
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={cellEditor?.cell.Level ?? ''}
              onChange={(e) => handleCellLevelChange(e.target.value as number | string)}
              displayEmpty
            >
              {SKILL_LEVELS.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {`Level ${lvl} — ${SKILL_LEVEL_META[lvl].label}`}
                </MenuItem>
              ))}
              <Divider component="li" />
              <MenuItem
                value={CLEAR_SENTINEL}
                disabled={cellEditor?.cell.Level == null}
              >
                Clear (no skill recorded)
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Popover>

      {/* Snackbar toasts */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.severity === 'error' ? 6000 : 3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast?.severity ?? 'info'}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};
